import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Badge,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  FormErrorMessage,
  Spinner,
  Center,
  Link,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { useAuth } from '../context/AuthContext';
import { useQuery } from 'react-query';

// Import the Package interface from packageService
import { Package } from '../services/packageService';
// Import services for API calls
import {
  getUserPackages,
  createPackage,
  updatePackage,
  deletePackage,
  publishPackage,
  unpublishPackage
} from '../services/userPackageService';

interface PackageFormData {
  name: string;
  description: string;
  github_url: string;
  version?: string;
  root_dir?: string;
}

export default function UserPackagesPage() {
  const { user } = useAuth();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // State
  const [formData, setFormData] = useState<PackageFormData>({
    name: '',
    description: '',
    github_url: '',
    version: '',
    root_dir: '/',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal controls
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  // Fetch packages data
  const { data: packages, isLoading, isError } = useQuery('userPackages', getUserPackages, {
    enabled: !!user,
  });

  // Form handlers
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Package name is required';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    
    if (!formData.github_url.trim()) {
      errors.github_url = 'GitHub URL is required';
    } else if (!formData.github_url.match(/^https:\/\/github\.com\/[\w-]+\/[\w-]+\/?$/i)) {
      errors.github_url = 'Please enter a valid GitHub repository URL';
    }
    
    if (isCreateOpen && !formData.version?.trim()) {
      errors.version = 'Initial version is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      await createPackage({
        name: formData.name,
        description: formData.description,
        github_url: formData.github_url,
        version: formData.version || '0.1.0',
        root_dir: formData.root_dir,
      });
      
      toast({
        title: 'Package created',
        description: 'Your package has been created successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      setIsSubmitting(false);
      onCreateClose();
      resetForm();
    } catch (error) {
      toast({
        title: 'Error creating package',
        description: error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !selectedPackage) return;
    
    setIsSubmitting(true);
    
    try {
      await updatePackage(selectedPackage.id, {
        description: formData.description,
        github_url: formData.github_url,
        root_dir: formData.root_dir,
      });
      
      toast({
        title: 'Package updated',
        description: 'Your package has been updated successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      setIsSubmitting(false);
      onEditClose();
    } catch (error) {
      toast({
        title: 'Error updating package',
        description: error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPackage) return;
    
    setIsSubmitting(true);
    
    try {
      await deletePackage(selectedPackage.id);
      
      toast({
        title: 'Package deleted',
        description: 'Your package has been deleted successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      setIsSubmitting(false);
      onDeleteClose();
    } catch (error) {
      toast({
        title: 'Error deleting package',
        description: error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  const handlePublishToggle = async (pkg: Package) => {
    try {
      if (pkg.is_published) {
        await unpublishPackage(pkg.id);
      } else {
        await publishPackage(pkg.id);
      }
      
      toast({
        title: pkg.is_published ? 'Package unpublished' : 'Package published',
        description: pkg.is_published 
          ? 'Your package is now private.' 
          : 'Your package is now available in the registry.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'An error occurred',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const openEditModal = (pkg: Package) => {
    setSelectedPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description,
      github_url: pkg.github_url,
      root_dir: pkg.root_dir || '/',
    });
    onEditOpen();
  };

  const openDeleteModal = (pkg: Package) => {
    setSelectedPackage(pkg);
    onDeleteOpen();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      github_url: '',
      version: '',
      root_dir: '/',
    });
    setFormErrors({});
  };

  if (isLoading) {
    return (
      <Center py={10}>
        <Spinner size="xl" color="brand.500" thickness="4px" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Container maxW="container.xl" py={8}>
        <Box p={5} shadow="md" borderWidth="1px" borderRadius="md">
          <Heading mb={4} size="md">Error loading packages</Heading>
          <Text>There was an error loading your packages. Please try again later.</Text>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <Heading>My MCP Packages</Heading>
        <Button 
          leftIcon={<AddIcon />} 
          colorScheme="brand" 
          onClick={() => {
            resetForm();
            onCreateOpen();
          }}
        >
          Create Package
        </Button>
      </Flex>
      
      <Text mb={8} fontSize="lg" color="gray.600" _dark={{ color: 'gray.400' }}>
        Manage your MCP server packages and publish them to the registry
      </Text>

      {!packages || packages.length === 0 ? (
        <Box my={10} textAlign="center" p={8} borderWidth="1px" borderRadius="lg">
          <Heading size="md" mb={4}>You haven't created any packages yet</Heading>
          <Text fontSize="lg" mb={6}>Get started by creating your first MCP package</Text>
          <Button 
            colorScheme="brand" 
            leftIcon={<AddIcon />}
            size="lg"
            onClick={() => {
              resetForm();
              onCreateOpen();
            }}
          >
            Create Your First Package
          </Button>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {packages.map((pkg) => (
            <Card 
              key={pkg.id} 
              bg={cardBg} 
              borderColor={borderColor} 
              borderWidth="1px"
              borderRadius="lg"
              overflow="hidden"
              transition="all 0.2s"
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
            >
              <CardHeader>
                <Flex justify="space-between" align="start">
                  <Box>
                    <Heading size="md" color="brand.500" mb={1}>{pkg.name}</Heading>
                    <Flex gap={2}>
                      <Badge colorScheme="blue" borderRadius="full">v{pkg.latest_version}</Badge>
                      <Badge 
                        colorScheme={pkg.is_published ? "green" : "orange"} 
                        borderRadius="full"
                      >
                        {pkg.is_published ? "Published" : "Draft"}
                      </Badge>
                    </Flex>
                  </Box>
                </Flex>
              </CardHeader>
              <CardBody pt={0}>
                <Text noOfLines={3} mb={2}>{pkg.description}</Text>
                <Link href={pkg.github_url} isExternal color="brand.500" fontSize="sm">
                  GitHub Repository <ExternalLinkIcon mx="2px" />
                </Link>
              </CardBody>
              <CardFooter>
                <Stack width="100%" spacing={2}>
                  <Text fontSize="sm" color="gray.500">Downloads: {pkg.downloads.toLocaleString()}</Text>
                  <Flex gap={2} wrap="wrap">
                    <Button 
                      size="sm" 
                      leftIcon={<EditIcon />} 
                      onClick={() => openEditModal(pkg)}
                    >
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      colorScheme={pkg.is_published ? "orange" : "green"}
                      onClick={() => handlePublishToggle(pkg)}
                    >
                      {pkg.is_published ? "Unpublish" : "Publish"}
                    </Button>
                    <Button 
                      size="sm" 
                      colorScheme="red" 
                      variant="outline"
                      leftIcon={<DeleteIcon />} 
                      onClick={() => openDeleteModal(pkg)}
                    >
                      Delete
                    </Button>
                  </Flex>
                </Stack>
              </CardFooter>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {/* Create Package Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleCreateSubmit}>
            <ModalHeader>Create New Package</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl isRequired isInvalid={!!formErrors.name} mb={4}>
                <FormLabel>Package Name</FormLabel>
                <Input 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange}
                  placeholder="e.g., my-awesome-mcp"
                />
                <FormErrorMessage>{formErrors.name}</FormErrorMessage>
              </FormControl>
              
              <FormControl isRequired isInvalid={!!formErrors.description} mb={4}>
                <FormLabel>Description</FormLabel>
                <Textarea 
                  name="description" 
                  value={formData.description} 
                  onChange={handleChange}
                  placeholder="A brief description of what your MCP server does"
                  rows={3}
                />
                <FormErrorMessage>{formErrors.description}</FormErrorMessage>
              </FormControl>
              
              <FormControl isRequired isInvalid={!!formErrors.github_url} mb={4}>
                <FormLabel>GitHub Repository URL</FormLabel>
                <Input 
                  name="github_url" 
                  value={formData.github_url} 
                  onChange={handleChange}
                  placeholder="https://github.com/username/repo"
                />
                <FormErrorMessage>{formErrors.github_url}</FormErrorMessage>
              </FormControl>
              
              <FormControl isRequired isInvalid={!!formErrors.version} mb={4}>
                <FormLabel>Initial Version</FormLabel>
                <Input 
                  name="version" 
                  value={formData.version} 
                  onChange={handleChange}
                  placeholder="e.g., 0.1.0"
                />
                <FormErrorMessage>{formErrors.version}</FormErrorMessage>
              </FormControl>
              
              <FormControl mb={4}>
                <FormLabel>Root Directory</FormLabel>
                <Input 
                  name="root_dir" 
                  value={formData.root_dir} 
                  onChange={handleChange}
                  placeholder="e.g., /src or / for root"
                />
                <Text fontSize="sm" color="gray.500">
                  Specify the subdirectory where the MCP server is located in the repository.
                  Use "/" for root directory.
                </Text>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onCreateClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                colorScheme="brand" 
                isLoading={isSubmitting}
              >
                Create Package
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Edit Package Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleEditSubmit}>
            <ModalHeader>Edit Package</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl mb={4}>
                <FormLabel>Package Name</FormLabel>
                <Input 
                  name="name" 
                  value={formData.name} 
                  isReadOnly
                  bg="gray.50"
                  _dark={{ bg: 'gray.700' }}
                />
                <Text fontSize="sm" color="gray.500">Package name cannot be changed</Text>
              </FormControl>
              
              <FormControl isRequired isInvalid={!!formErrors.description} mb={4}>
                <FormLabel>Description</FormLabel>
                <Textarea 
                  name="description" 
                  value={formData.description} 
                  onChange={handleChange}
                  placeholder="A brief description of what your MCP server does"
                  rows={3}
                />
                <FormErrorMessage>{formErrors.description}</FormErrorMessage>
              </FormControl>
              
              <FormControl isRequired isInvalid={!!formErrors.github_url} mb={4}>
                <FormLabel>GitHub Repository URL</FormLabel>
                <Input 
                  name="github_url" 
                  value={formData.github_url} 
                  onChange={handleChange}
                  placeholder="https://github.com/username/repo"
                />
                <FormErrorMessage>{formErrors.github_url}</FormErrorMessage>
              </FormControl>
              
              <FormControl mb={4}>
                <FormLabel>Root Directory</FormLabel>
                <Input 
                  name="root_dir" 
                  value={formData.root_dir} 
                  onChange={handleChange}
                  placeholder="e.g., /src or / for root"
                />
                <Text fontSize="sm" color="gray.500">
                  Specify the subdirectory where the MCP server is located in the repository.
                  Use "/" for root directory.
                </Text>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onEditClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                colorScheme="brand" 
                isLoading={isSubmitting}
              >
                Save Changes
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Package Confirmation */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Package
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this package? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleDelete} 
                ml={3}
                isLoading={isSubmitting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
}
